import {
  Combobox,
  ComboboxEmpty,
  ComboboxItem,
  Flex,
  Skeleton,
  SkeletonBox,
  useComboboxFilter,
} from '@utilitywarehouse/hearth-react';
import React, {
  memo,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { IOTSProvider } from '@src/types/oneTouchSwitching';

export interface ProviderSelectProps {
  providers: IOTSProvider[];
  selectedProvider: IOTSProvider | null;
  setSelectedProvider: (value: IOTSProvider | null) => void;
  disabled?: boolean;
  isLoading?: boolean;
  onOpen?: () => void;
  children?:
    | React.ReactNode
    | ((props: { setOpen: (open: boolean) => void }) => React.ReactNode);
}

interface ProviderResultsProps {
  providers: IOTSProvider[];
  searchQuery: string;
}

const ProviderResults = memo(
  ({ providers, searchQuery }: ProviderResultsProps) => {
    const { contains } = useComboboxFilter();
    const filteredProviders = useMemo(
      () =>
        providers
          .filter((provider) => contains(provider, searchQuery, (p) => p.name))
          .sort((a, b) => a.name.localeCompare(b.name)),
      [providers, searchQuery, contains]
    );

    return (
      <>
        {filteredProviders.length === 0 && (
          <ComboboxEmpty>No providers found</ComboboxEmpty>
        )}
        {filteredProviders.map((provider) => (
          <ComboboxItem value={provider} key={provider.id}>
            {provider.name}
          </ComboboxItem>
        ))}
      </>
    );
  }
);

export const ProviderSelect: React.FC<ProviderSelectProps> = ({
  providers,
  selectedProvider,
  setSelectedProvider,
  disabled,
  children,
  isLoading = false,
  onOpen,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(selectedProvider?.name ?? '');

  useEffect(() => {
    if (open && onOpen) {
      onOpen();
    }
  }, [open, onOpen]);

  // Keep the input text in sync when the selection changes externally
  // (reset, "provider not listed" toggle, or a remote-appointment sync).
  useEffect(() => {
    setInputValue(selectedProvider?.name ?? '');
  }, [selectedProvider]);

  // Defer filtering so typing stays responsive against the large provider list.
  const deferredInputValue = useDeferredValue(inputValue);

  return (
    <div>
      <Combobox<IOTSProvider>
        label="Who’s your current broadband provider?"
        placeholder="Search for your provider"
        disabled={disabled}
        value={selectedProvider}
        onValueChange={setSelectedProvider}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        itemToStringLabel={(provider) => provider.name}
        isItemEqualToValue={(item, value) => item.id === value.id}
        id="OTSComboBox"
        required={!disabled}
        open={open}
        onOpenChange={setOpen}
      >
        {isLoading && (
          <Skeleton loadingTitle="Loading providers">
            <Flex
              direction="column"
              gap="100"
              alignItems="center"
              paddingX="100"
              paddingY="100"
            >
              {new Array(4).fill(0).map((_, index) => (
                <SkeletonBox
                  key={index}
                  height="32px"
                  borderRadius="sm"
                  style={{ width: '100%' }}
                />
              ))}
            </Flex>
          </Skeleton>
        )}

        {!isLoading && (
          <>
            <ProviderResults
              providers={providers}
              searchQuery={deferredInputValue}
            />
            {typeof children === 'function' ? children({ setOpen }) : children}
          </>
        )}
      </Combobox>
    </div>
  );
};
